

import React, { useMemo, useEffect, useState } from 'react';
import { MapContainer, TileLayer, useMap, useMapEvents, CircleMarker, Tooltip, Marker, Popup, Polygon, Polyline } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet.heat';
import MapLibreGLLayer from './yard-layout/MapLibreGLLayer';

// Red (worst) to green (best) by value between min and max
function getColorRedToGreen(value, min, max) {
    if (min == null || max == null || min === max) return '#22c55e';
    const t = Math.max(0, Math.min(1, (value - min) / (max - min)));
    const r = Math.round(255 * (1 - t));
    const g = Math.round(255 * t);
    return `rgb(${r},${g},0)`;
}

const getLteColor = (dbm) => { if (dbm <= -118) return "#f60808"; if (dbm > -118 && dbm <= -98) return "#fcdd17"; if (dbm > -98) return "#2ac91f"; return "#808080"; };
const getWiFiColor = (percentage) => { if (percentage > 0 && percentage <= 10) return "#f60808"; if (percentage > 10 && percentage <= 20) return "#d58713"; if (percentage > 20 && percentage <= 60) return "#d2cb42"; if (percentage > 60) return "#2ac91f"; return "#808080"; };
const getTowerIcon = (carrier) => { let color = '#808080'; if (carrier === 'AT&T Mobility') color = '#1d5acd'; if (carrier === 'T-Mobile USA') color = '#c62578'; if (carrier === 'Verizon') color = '#b80f22'; const iconHtml = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="${color}" width="24px" height="24px"><path d="M12 2L2 22h20L12 2z"/></svg>`; return L.divIcon({ html: iconHtml, className: 'custom-leaflet-icon', iconSize: [24, 24], iconAnchor: [12, 24] }); };
const getPulseIcon = (color) => L.divIcon({
    html: `<div class="latest-point-pulse" style="--pulse-color: ${color};"><div class="latest-point-pulse-dot"></div></div>`,
    className: 'latest-point-pulse-icon',
    iconSize: [30, 30],
    iconAnchor: [15, 15],
});
function MapUpdater({ center, zoom }) { const map = useMap(); useEffect(() => { if (center) { map.setView(center, zoom); } }, [center, zoom, map]); return null; }

function ZoomControls() {
    const map = useMap();
    return (
        <div className="absolute bottom-4 left-4 z-[1000] flex flex-col gap-1">
            <button
                type="button"
                onClick={() => map.zoomIn()}
                className="bg-white rounded shadow-md w-8 h-8 flex items-center justify-center text-gray-700 hover:bg-gray-100 border border-gray-300 font-bold text-lg"
                title="Zoom in"
            >
                +
            </button>
            <button
                type="button"
                onClick={() => map.zoomOut()}
                className="bg-white rounded shadow-md w-8 h-8 flex items-center justify-center text-gray-700 hover:bg-gray-100 border border-gray-300 font-bold text-lg"
                title="Zoom out"
            >
                −
            </button>
        </div>
    );
}

// Free-hand drawing: track mouse down + move to draw continuous line
function DrawZoneHandler({ enabled, onDrawPoint, onDrawStart, onDrawEnd }) {
    const map = useMap();
    const isDrawingRef = React.useRef(false);
    useEffect(() => {
        if (enabled) {
            map.dragging.disable();
            map.doubleClickZoom.disable();
        } else {
            map.dragging.enable();
            map.doubleClickZoom.enable();
        }
    }, [enabled, map]);
    useMapEvents({
        mousedown: (e) => {
            if (enabled) {
                isDrawingRef.current = true;
                if (onDrawStart) onDrawStart();
                if (onDrawPoint) onDrawPoint(e.latlng.lat, e.latlng.lng);
            }
        },
        mousemove: (e) => {
            if (enabled && isDrawingRef.current && onDrawPoint) {
                onDrawPoint(e.latlng.lat, e.latlng.lng);
            }
        },
        mouseup: () => {
            if (enabled && isDrawingRef.current) {
                isDrawingRef.current = false;
                if (onDrawEnd) onDrawEnd();
            }
        },
    });
    return null;
}

// Helper component for the heatmap layer
const HeatmapLayer = ({ data }) => {
    const map = useMap();

    useEffect(() => {
        if (data && data.length > 0) {
           
            const heatLayer = L.heatLayer(data, {
                radius: 25,
                blur: 15,
                maxZoom: 18,
            }).addTo(map);

            return () => {
                map.removeLayer(heatLayer);
            };
        }
    }, [data, map]);

    return null;
};


const LeafletMapComponent = ({
    center, zoom, data, dataType, signalMetric, speedColorBy, towerData,
    drawModeEnabled = false, drawnPolygon = [], onDrawPoint, zoneStats,
    onToggleDrawMode, onClearPolygon, isPenActive = true,
    onSetPenActive, allSignalData = [], allSpeedData = [],
    yardTileUrl = null,
}) => {
    const [hoverInfo, setHoverInfo] = useState(null);

    // Helper to find nearby data point (within ~50 meters)
    const findNearbyPoint = (lat, lng, dataArray, threshold = 0.0005) => {
        if (!dataArray || dataArray.length === 0) return null;
        for (const p of dataArray) {
            const latDiff = Math.abs(p.lat - lat);
            const lngDiff = Math.abs(p.lng - lng);
            if (latDiff < threshold && lngDiff < threshold) {
                return p;
            }
        }
        return null;
    };
    const maptilerApiKey = process.env.REACT_APP_MAPTILER_API_KEY;
    const satelliteTileUrl = `https://api.maptiler.com/maps/satellite/{z}/{x}/{y}.jpg?key=${maptilerApiKey}`;
    const attribution = '&copy; <a href="https://www.maptiler.com/">MapTiler</a> &copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors';

    const { signalMin, signalMax } = useMemo(() => {
        if (!data || data.length === 0 || dataType !== 'signal_strength') return { signalMin: null, signalMax: null };
        const vals = data.map((p) => p.value).filter((v) => v != null && !Number.isNaN(Number(v)));
        if (vals.length === 0) return { signalMin: null, signalMax: null };
        return { signalMin: Math.min(...vals), signalMax: Math.max(...vals) };
    }, [data, dataType]);

    const { speedMin, speedMax } = useMemo(() => {
        if (!data || data.length === 0 || dataType !== 'internet_speed') return { speedMin: null, speedMax: null };
        const vals = data.map((p) => (p.value != null ? Number(p.value) : NaN)).filter((v) => !Number.isNaN(v));
        if (vals.length === 0) return { speedMin: null, speedMax: null };
        return { speedMin: Math.min(...vals), speedMax: Math.max(...vals) };
    }, [data, dataType]);

    const latestSpeedPoint = useMemo(() => {
        const points = allSpeedData && allSpeedData.length ? allSpeedData : [];
        if (!points.length) return null;
        return points.reduce((latest, p) => {
            if (!latest) return p;
            const latestTs = latest.timestamp ? new Date(latest.timestamp).getTime() : 0;
            const currTs = p.timestamp ? new Date(p.timestamp).getTime() : 0;
            return currTs > latestTs ? p : latest;
        }, null);
    }, [allSpeedData]);

    const latestSignalPoint = useMemo(() => {
        const points = allSignalData && allSignalData.length ? allSignalData : [];
        if (!points.length) return null;
        return points.reduce((latest, p) => {
            if (!latest) return p;
            const latestTs = latest.timestamp ? new Date(latest.timestamp).getTime() : 0;
            const currTs = p.timestamp ? new Date(p.timestamp).getTime() : 0;
            return currTs > latestTs ? p : latest;
        }, null);
    }, [allSignalData]);

    const renderDataLayer = () => {
        if (!data || data.length === 0) return null;

        if (dataType === 'signal_strength') {
            return data.map((point, index) => {
                const v = point.value != null ? Number(point.value) : null;
                const color = v != null && !Number.isNaN(v) ? getColorRedToGreen(v, signalMin, signalMax) : '#94a3b8';
                const label = signalMetric === 'lte' ? `${point.value != null ? point.value : 'N/A'} dBm` : `${point.value != null ? point.value : 'N/A'}%`;
                const ts = point.timestamp ? new Date(point.timestamp).toLocaleString() : '';
                const opacity = point.opacity != null ? point.opacity : 0.85;
                const isLatest =
                    latestSignalPoint &&
                    latestSignalPoint.lat === point.lat &&
                    latestSignalPoint.lng === point.lng &&
                    latestSignalPoint.timestamp === point.timestamp;
                return (
                    <React.Fragment key={`sig-${index}-${point.lat}-${point.lng}`}>
                        {isLatest && (
                            <Marker
                                position={[point.lat, point.lng]}
                                icon={getPulseIcon('#2563eb')}
                                interactive={false}
                            />
                        )}
                        <CircleMarker
                            center={[point.lat, point.lng]}
                            radius={isLatest ? 8 : 6}
                            pathOptions={{ fillColor: color, color: isLatest ? '#2563eb' : '#1f2937', fillOpacity: opacity, weight: isLatest ? 2 : 1 }}
                            eventHandlers={{
                                mouseover: () => {
                                    const nearbySpeed = findNearbyPoint(point.lat, point.lng, allSpeedData);
                                    setHoverInfo({ type: 'signal_strength', point, label, timestamp: ts, metric: signalMetric, nearbySpeed });
                                },
                                mouseout: () => setHoverInfo(null),
                            }}
                        >
                            <Popup>
                                <div className="text-sm">
                                    <div><strong>{signalMetric === 'lte' ? 'LTE signal' : 'Wi‑Fi signal'}:</strong> {label}</div>
                                    {ts && <div className="text-gray-500 mt-1">{ts}</div>}
                                    {isLatest && <div className="text-blue-600 mt-1"><strong>Latest signal point</strong></div>}
                                </div>
                            </Popup>
                        </CircleMarker>
                    </React.Fragment>
                );
            });
        }

        if (dataType === 'internet_speed') {
            return data.map((point, index) => {
                const v = point.value != null ? Number(point.value) : null;
                const color = v != null && !Number.isNaN(v) ? getColorRedToGreen(v, speedMin, speedMax) : '#94a3b8';
                const ts = point.timestamp ? new Date(point.timestamp).toLocaleString() : '';
                const isWifi = (point.connection_type || '').toLowerCase().includes('wifi');
                const networkLabel = isWifi ? 'Wi‑Fi' : (point.carrier_name || point.connection_type || 'N/A');

                const isLatest =
                    latestSpeedPoint &&
                    latestSpeedPoint.lat === point.lat &&
                    latestSpeedPoint.lng === point.lng &&
                    latestSpeedPoint.timestamp === point.timestamp;

                const radius = isLatest ? 10 : 6;
                const borderColor = isLatest ? '#ef4444' : '#1f2937';
                const opacity = isLatest ? 1.0 : (point.opacity != null ? point.opacity : 0.85);

                return (
                    <React.Fragment key={`spd-${index}-${point.lat}-${point.lng}`}>
                        {isLatest && (
                            <Marker
                                position={[point.lat, point.lng]}
                                icon={getPulseIcon('#ef4444')}
                                interactive={false}
                            />
                        )}
                        <CircleMarker
                            center={[point.lat, point.lng]}
                            radius={radius}
                            pathOptions={{ fillColor: color, color: borderColor, fillOpacity: opacity, weight: isLatest ? 2 : 1 }}
                            eventHandlers={{
                                mouseover: () => {
                                    const nearbySignal = findNearbyPoint(point.lat, point.lng, allSignalData);
                                    setHoverInfo({ type: 'internet_speed', point, timestamp: ts, isWifi, networkLabel, nearbySignal });
                                },
                                mouseout: () => setHoverInfo(null),
                            }}
                        >
                            <Popup>
                                <div className="text-sm">
                                    <div><strong>Upload:</strong> {point.upload_mbps != null ? `${Number(point.upload_mbps).toFixed(1)} Mbps` : 'N/A'}</div>
                                    <div><strong>Download:</strong> {point.download_mbps != null ? `${Number(point.download_mbps).toFixed(1)} Mbps` : 'N/A'}</div>
                                    <div><strong>Signal:</strong> {point.signal_rsrp_dbm != null ? `${point.signal_rsrp_dbm} dBm` : 'N/A'}</div>
                                    <div><strong>{isWifi ? 'Network' : 'Carrier'}:</strong> {networkLabel}</div>
                                    <div className="text-gray-500 mt-1">{ts}</div>
                                    {isLatest && <div className="text-red-600 mt-1"><strong>Latest speed point</strong></div>}
                                </div>
                            </Popup>
                        </CircleMarker>
                    </React.Fragment>
                );
            });
        }

        if (dataType === 'location') {
            const heatmapPoints = data.map(point => [point.lat, point.lng, 5.0]);
            return <HeatmapLayer data={heatmapPoints} />;
        }
        if (dataType === 'speed_density') {
            const heatmapPoints = data.map(point => [point.lat, point.lng, 5.0]);
            return <HeatmapLayer data={heatmapPoints} />;
        }
        if (dataType === 'speed_performance') {
            const values = data.map(p => (p.value != null ? Number(p.value) : 5)).filter(v => !Number.isNaN(v));
            const maxV = values.length ? Math.max(...values, 1) : 1;
            const heatmapPoints = data.map(point => {
                const v = point.value != null ? Number(point.value) : 5;
                const intensity = Number.isNaN(v) ? 5 : Math.max(1, (v / maxV) * 12);
                return [point.lat, point.lng, intensity];
            });
            return <HeatmapLayer data={heatmapPoints} />;
        }
        if (dataType === 'speed_wifi_cellular') {
            return data.map((point, index) => {
                const isWifi = (point.connection_type || '').toLowerCase().includes('wifi');
                const fill = isWifi ? '#22c55e' : '#f59e0b';
                const color = isWifi ? '#16a34a' : '#d97706';
                return (
                    <CircleMarker
                        key={`sc-${index}-${point.lat}-${point.lng}`}
                        center={[point.lat, point.lng]}
                        radius={6}
                        pathOptions={{ fillColor: fill, color, fillOpacity: 0.8, weight: 1.5 }}
                    >
                        <Tooltip>
                            {point.connection_type || 'N/A'} – Upload: {point.upload_mbps != null ? `${point.upload_mbps.toFixed(1)} Mbps` : 'N/A'}
                        </Tooltip>
                    </CircleMarker>
                );
            });
        }
        if (dataType === 'lte') { return data.map((point, index) => ( <CircleMarker key={`lte-${index}`} center={[point.lat, point.lng]} radius={5} pathOptions={{ color: getLteColor(point.value), fillOpacity: 0.7 }}><Tooltip>LTE Signal: {point.value} dBm</Tooltip></CircleMarker> )); }
        if (dataType === 'wifi') { return data.map((point, index) => ( <CircleMarker key={`wifi-${index}`} center={[point.lat, point.lng]} radius={5} pathOptions={{ color: getWiFiColor(point.value), fillOpacity: 0.7 }}><Tooltip>Wi-Fi Signal: {point.value}%</Tooltip></CircleMarker> )); }
        return null;
    };

    const renderTowers = () => { if (!towerData || towerData.length === 0) return null; return towerData.map((tower, index) => ( <Marker key={`tower-${index}`} position={[tower.lat, tower.lon]} icon={getTowerIcon(tower.carrier)}><Popup><strong>Carrier:</strong> {tower.carrier}<br/><strong>Range:</strong> {tower.rangeCell}m</Popup></Marker> )); };

    const renderHoverPanel = () => {
        if (!hoverInfo) return null;
        if (hoverInfo.type === 'signal_strength') {
            const { point, label, timestamp, metric, nearbySpeed } = hoverInfo;
            return (
                <div className="absolute top-2 left-2 z-[1000] bg-white bg-opacity-95 rounded-lg shadow-lg px-4 py-3 text-xs max-w-sm pointer-events-none border border-gray-200">
                    <div className="font-bold text-gray-800 mb-2 pb-1 border-b border-gray-200">Location Data</div>
                    
                    {/* Signal Section */}
                    <div className="mb-2">
                        <div className="font-semibold text-blue-700 mb-1">{metric === 'lte' ? 'LTE Signal' : 'Wi‑Fi Signal'}</div>
                        <div className="text-gray-700">{label}</div>
                        {timestamp && <div className="text-gray-400 text-[10px] mt-0.5">{timestamp}</div>}
                    </div>
                    
                    {/* Speed Section (if nearby data found) */}
                    {nearbySpeed ? (
                        <div className="pt-2 border-t border-gray-100">
                            <div className="font-semibold text-green-700 mb-1">Speed Test</div>
                            <div><strong>Download:</strong> {nearbySpeed.download_mbps != null ? `${Number(nearbySpeed.download_mbps).toFixed(1)} Mbps` : 'N/A'}</div>
                            <div><strong>Upload:</strong> {nearbySpeed.upload_mbps != null ? `${Number(nearbySpeed.upload_mbps).toFixed(1)} Mbps` : 'N/A'}</div>
                            <div><strong>Connection:</strong> {nearbySpeed.connection_type || 'N/A'}</div>
                            {nearbySpeed.timestamp && <div className="text-gray-400 text-[10px] mt-0.5">{new Date(nearbySpeed.timestamp).toLocaleString()}</div>}
                        </div>
                    ) : (
                        <div className="pt-2 border-t border-gray-100 text-gray-400 italic">No speed test data at this location</div>
                    )}
                </div>
            );
        }
        if (hoverInfo.type === 'internet_speed') {
            const { point, timestamp, isWifi, networkLabel, nearbySignal } = hoverInfo;
            return (
                <div className="absolute top-2 left-2 z-[1000] bg-white bg-opacity-95 rounded-lg shadow-lg px-4 py-3 text-xs max-w-sm pointer-events-none border border-gray-200">
                    <div className="font-bold text-gray-800 mb-2 pb-1 border-b border-gray-200">Location Data</div>
                    
                    {/* Speed Section */}
                    <div className="mb-2">
                        <div className="font-semibold text-green-700 mb-1">Speed Test</div>
                        <div><strong>Download:</strong> {point.download_mbps != null ? `${Number(point.download_mbps).toFixed(1)} Mbps` : 'N/A'}</div>
                        <div><strong>Upload:</strong> {point.upload_mbps != null ? `${Number(point.upload_mbps).toFixed(1)} Mbps` : 'N/A'}</div>
                        <div><strong>{isWifi ? 'Network' : 'Carrier'}:</strong> {networkLabel}</div>
                        {timestamp && <div className="text-gray-400 text-[10px] mt-0.5">{timestamp}</div>}
                    </div>
                    
                    {/* Signal Section (if nearby data found) */}
                    {nearbySignal ? (
                        <div className="pt-2 border-t border-gray-100">
                            <div className="font-semibold text-blue-700 mb-1">Signal Strength</div>
                            <div><strong>LTE:</strong> {nearbySignal.value != null ? `${nearbySignal.value} dBm` : 'N/A'}</div>
                            {nearbySignal.timestamp && <div className="text-gray-400 text-[10px] mt-0.5">{new Date(nearbySignal.timestamp).toLocaleString()}</div>}
                        </div>
                    ) : (
                        <div className="pt-2 border-t border-gray-100 text-gray-400 italic">No signal data at this location</div>
                    )}
                </div>
            );
        }
        return null;
    };

    const polygonPositions = useMemo(() => {
        if (!drawnPolygon || drawnPolygon.length < 3) return null;
        return drawnPolygon.map(([lat, lng]) => [lat, lng]);
    }, [drawnPolygon]);

    const renderZoneStatsPanel = () => {
        if (!zoneStats || !drawnPolygon || drawnPolygon.length < 3) return null;
        const { inside, outside } = zoneStats;
        const isSpeed = dataType === 'internet_speed';
        return (
            <div className="absolute bottom-2 left-2 z-[1000] bg-white/95 rounded-lg shadow-lg p-3 text-xs max-w-sm pointer-events-auto border border-gray-200">
                <div className="font-semibold mb-2 text-gray-800">Zone comparison</div>
                <div className="grid grid-cols-2 gap-3">
                    <div className="border border-blue-200 rounded p-2 bg-blue-50/50">
                        <div className="font-medium text-blue-800 mb-1">Inside zone</div>
                        <div>Points: {inside?.count ?? 0}</div>
                        {isSpeed && (
                            <>
                                <div>Avg down: {inside?.avgDownload != null ? `${inside.avgDownload} Mbps` : '—'}</div>
                                <div>Avg up: {inside?.avgUpload != null ? `${inside.avgUpload} Mbps` : '—'}</div>
                            </>
                        )}
                        {!isSpeed && <div>Median signal: {inside?.medianSignal != null ? `${inside.medianSignal}` : '—'}</div>}
                        <div>% below SLA: {inside?.pctBelowSla != null ? `${inside.pctBelowSla}%` : '—'}</div>
                    </div>
                    <div className="border border-gray-200 rounded p-2 bg-gray-50/50">
                        <div className="font-medium text-gray-800 mb-1">Rest of yard</div>
                        <div>Points: {outside?.count ?? 0}</div>
                        {isSpeed && (
                            <>
                                <div>Avg down: {outside?.avgDownload != null ? `${outside.avgDownload} Mbps` : '—'}</div>
                                <div>Avg up: {outside?.avgUpload != null ? `${outside.avgUpload} Mbps` : '—'}</div>
                            </>
                        )}
                        {!isSpeed && <div>Median signal: {outside?.medianSignal != null ? `${outside.medianSignal}` : '—'}</div>}
                        <div>% below SLA: {outside?.pctBelowSla != null ? `${outside.pctBelowSla}%` : '—'}</div>
                    </div>
                </div>
            </div>
        );
    };

    const renderDrawControls = () => {
        if (!drawModeEnabled) {
            return (
                <button
                    type="button"
                    onClick={() => onToggleDrawMode && onToggleDrawMode(true)}
                    className="absolute top-2 right-2 z-[1000] bg-white rounded-lg shadow-lg p-2 hover:bg-gray-100 border border-gray-200"
                    title="Enable drawing"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5 text-gray-700">
                        <path d="M12 19l7-7 3 3-7 7-3-3z"/>
                        <path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z"/>
                        <path d="M2 2l7.586 7.586"/>
                        <circle cx="11" cy="11" r="2"/>
                    </svg>
                </button>
            );
        }
        return (
            <div className="absolute top-2 right-2 z-[1000] flex flex-col gap-2">
                <div className="bg-white rounded-lg shadow-lg p-2 flex gap-1 border border-gray-200">
                    <button
                        type="button"
                        onClick={() => onSetPenActive && onSetPenActive(true)}
                        className={`px-3 py-1.5 rounded text-sm font-medium flex items-center gap-1 ${isPenActive ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                        title="Pen - draw on map"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
                            <path d="M12 19l7-7 3 3-7 7-3-3z"/>
                            <path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z"/>
                        </svg>
                        Pen
                    </button>
                    <button
                        type="button"
                        onClick={() => onSetPenActive && onSetPenActive(false)}
                        className={`px-3 py-1.5 rounded text-sm font-medium flex items-center gap-1 ${!isPenActive ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                        title="Move - pan the map"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
                            <polyline points="5 9 2 12 5 15"/>
                            <polyline points="9 5 12 2 15 5"/>
                            <polyline points="15 19 12 22 9 19"/>
                            <polyline points="19 9 22 12 19 15"/>
                            <line x1="2" y1="12" x2="22" y2="12"/>
                            <line x1="12" y1="2" x2="12" y2="22"/>
                        </svg>
                        Move
                    </button>
                </div>
                <div className="bg-white rounded-lg shadow-lg p-2 flex gap-1 border border-gray-200">
                    {drawnPolygon && drawnPolygon.length > 0 && (
                        <button
                            type="button"
                            onClick={() => onClearPolygon && onClearPolygon()}
                            className="px-3 py-1.5 rounded text-sm font-medium bg-red-500 text-white hover:bg-red-600"
                        >
                            Clear
                        </button>
                    )}
                    <button
                        type="button"
                        onClick={() => onToggleDrawMode && onToggleDrawMode(false)}
                        className="px-3 py-1.5 rounded text-sm font-medium bg-gray-500 text-white hover:bg-gray-600"
                    >
                        Close
                    </button>
                </div>
            </div>
        );
    };

    return (
        <div className="relative w-full h-full">
            {renderHoverPanel()}
            {renderZoneStatsPanel()}
            {renderDrawControls()}
            <MapContainer center={center} zoom={zoom} scrollWheelZoom={true} zoomControl={false} className="w-full h-full">
                <style>{`
                    .custom-leaflet-icon { background: transparent; border: 0; }
                    .zone-marker-icon { background: transparent !important; border: 0 !important; }
                    .latest-point-pulse-icon { background: transparent !important; border: 0 !important; }
                    .latest-point-pulse {
                        position: relative;
                        width: 30px;
                        height: 30px;
                        pointer-events: none;
                    }
                    .latest-point-pulse::before {
                        content: '';
                        position: absolute;
                        inset: 0;
                        border-radius: 9999px;
                        background: var(--pulse-color);
                        opacity: 0.35;
                        animation: latest-point-pulse 1.8s ease-out infinite;
                    }
                    .latest-point-pulse-dot {
                        position: absolute;
                        top: 50%;
                        left: 50%;
                        width: 10px;
                        height: 10px;
                        margin-top: -5px;
                        margin-left: -5px;
                        border-radius: 9999px;
                        background: var(--pulse-color);
                        border: 2px solid white;
                        box-shadow: 0 0 0 1px rgba(31,41,55,0.35);
                    }
                    @keyframes latest-point-pulse {
                        0% { transform: scale(0.4); opacity: 0.55; }
                        70% { transform: scale(1.8); opacity: 0; }
                        100% { transform: scale(1.8); opacity: 0; }
                    }
                `}</style>
                {yardTileUrl ? (
                    <MapLibreGLLayer styleUrl={yardTileUrl} />
                ) : (
                    <TileLayer attribution={attribution} url={satelliteTileUrl} />
                )}
                <MapUpdater center={center} zoom={zoom} />
                <ZoomControls />
                <DrawZoneHandler enabled={drawModeEnabled && isPenActive} onDrawPoint={onDrawPoint} />
                {drawnPolygon && drawnPolygon.length > 0 && (
                    <>
                        <Polyline positions={drawnPolygon.map(([lat, lng]) => [lat, lng])} pathOptions={{ color: '#2563eb', weight: 2, dashArray: '5,5' }} />
                        {polygonPositions && <Polygon positions={polygonPositions} pathOptions={{ color: '#2563eb', fillColor: '#3b82f6', fillOpacity: 0.2, weight: 2 }} />}
                    </>
                )}
                {renderDataLayer()}
                {renderTowers()}
            </MapContainer>
        </div>
    );
};

export default LeafletMapComponent;